import React, {useEffect, useMemo, useRef} from 'react';
import PropTypes from 'prop-types';
import cs from 'classnames';
import {FormattedMessage} from 'react-intl';

import {Dialog, DialogHeader, DialogBody, DialogFooter, Button} from '@stubhub/bamboo';

import useCountdown from './hooks/useCountdown';
import {getRedirectURL} from './utils';
import {CBT_MODAL_MODES, REDIRECTION_TIMEOUT, MODAL_TRACK_NAME} from './utils/constants';

import '../styles/index.scss';

const CBTRedirectionModal = ({
  className,
  overlayClassName,
  mode,
  isOpen,
  uri,
  location,
  keepSearch,
  modalRef,
  onClose,
  onRedirect,
}) => {
  const [countdown, cancel] = useCountdown(REDIRECTION_TIMEOUT, 0, isOpen);
  const redirectRef = useRef(null);
  const isOptional = mode === CBT_MODAL_MODES.OPTIONAL;
  const redirectionUrl = useMemo(
    () => getRedirectURL(uri, location, keepSearch, null, MODAL_TRACK_NAME),
    [uri, location, keepSearch]
  );

  const handleRedirect = (event) => {
    cancel();
    onRedirect && onRedirect(event);
  };

  const handleClose = (event) => {
    event && event.stopPropagation();
    cancel();
    onClose && onClose(event);
  };

  useEffect(() => {
    if (countdown === 0) {
      if (redirectRef.current) {
        redirectRef.current.dispatchEvent(new MouseEvent('mousedown', {bubbles: true}));
        redirectRef.current.click();
      }
    }
  }, [countdown]);

  return (
    <Dialog
      data-testid="cbt-redirect-dialog"
      className={cs('CBT-redirect-dialog', className)}
      overlayClassName={cs('CBT-redirect-dialog__overlay', overlayClassName)}
      isOpen={isOpen}
      closeOnOverlayClick={isOptional}
      closeOnEsc={isOptional}
      onDismiss={handleClose}
      size="lg"
      ref={modalRef}
    >
      <DialogHeader className="CBT-redirect-dialog__header" closeButton={isOptional} />
      <DialogBody className="CBT-redirect-dialog__body">
        <div className="CBT-redirect-dialog__body__content">
          <h1>
            <FormattedMessage id="CBTModule.modal.title" defaultMessage="You'll be travelling! How exciting" />
          </h1>
          <p>
            <FormattedMessage
              id="CBTModule.modal.description.globalSite"
              defaultMessage="This event is hosted on our global site: stubhub.ie."
            />
          </p>
          <p>
            <FormattedMessage
              id="CBTModule.modal.description.legal"
              defaultMessage="As that site is governed by Irish Legislation, certain features you may be used to seeing here may not be available."
            />
          </p>
          <p>
            <FormattedMessage
              id="CBTModule.modal.description.redirection"
              defaultMessage="You will be redirected automatically in {seconds, plural,=0 {0 seconds} one {1 second} other {# seconds}}."
              values={{seconds: countdown}}
            />
          </p>
        </div>
        <div className="CBT-redirect-dialog__body__image" />
      </DialogBody>
      <DialogFooter className="CBT-redirect-dialog__footer">
        {isOptional ? (
          <>
            <Button appearance="secondary" onClick={handleClose}>
              <FormattedMessage id="CBTModule.modal.button.stay" defaultMessage="Stay here" />
            </Button>
            <Button appearance="primary" href={redirectionUrl} onClick={handleRedirect} ref={redirectRef}>
              <FormattedMessage id="CBTModule.modal.button.go" defaultMessage="Go now" />
            </Button>
          </>
        ) : (
          <Button appearance="primary" href={redirectionUrl} onClick={handleRedirect} ref={redirectRef}>
            <FormattedMessage id="CBTModule.modal.button.continue" defaultMessage="Continue" />
          </Button>
        )}
      </DialogFooter>
    </Dialog>
  );
};

CBTRedirectionModal.propTypes = {
  /** Custom class name for the dialog */
  className: PropTypes.string,

  /** Custom class name for the dialog overlay */
  overlayClassName: PropTypes.string,

  /** Redirection modal mode: `optional` or `mandatory` */
  mode: PropTypes.oneOf([CBT_MODAL_MODES.OPTIONAL, CBT_MODAL_MODES.MANDATORY]),

  /** If the modal is open or not */
  isOpen: PropTypes.bool,

  /** Redirection uri. If not provided, / will be used instead */
  uri: PropTypes.string,

  /** Location where the modal has been shown */
  location: PropTypes.oneOf(['home', 'search', 'category', 'grouping', 'geo', 'venue', 'performer', 'event'])
    .isRequired,

  /** Keep search params when redirecting? */
  keepSearch: PropTypes.bool,

  /** Ref to the cbt modal */
  modalRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({current: PropTypes.any})]),

  /** Callback function when closing the modal */
  onClose: PropTypes.func,

  /** Callback function when redirect is made */
  onRedirect: PropTypes.func,
};

export default CBTRedirectionModal;
export {CBT_MODAL_MODES, getRedirectURL, MODAL_TRACK_NAME};
